import React from 'react';
import { Badge } from '@brighterion/br-ui';
import { Cell, Column } from 'react-table';
import _ from 'lodash';
import { DateService, NumberService } from 'Services';
import TwoDashes from 'Components/TwoDashes';
import FormattedConclusion from 'Components/FormattedConclusion';
import DashOrEllipText from 'Components/DashOrEllipText';
import RuleType from 'Components/RuleType';
import {
    renderDefault,
    renderLinkButton,
    renderProgressBar,
    renderModifiedIcon,
    renderTrashIcon,
    renderCommentIcon,
    renderTitleCase
} from './helperComponents';
import BcatsMaskedField from './BcatsMaskedField';

import styles from './index.css';

// if you want to be able to pass along an object (a React element or whatever)
// define a custom render type and add it to this array
// or you can just define a Cell renderer inside of the column config
const ALLOW_OBJECTS_FOR_RENDER_TYPE: Column['renderType'][] = ['modifiedIcon', 'datetime', 'overrideCell'];

// MIND YOU THIS WON'T WORK ONCE WE DECIDE TO USE A CONFIG BASED JSON
// we need to handle the case when renderOptions is a "function"
// TODO: migrate renderOptions away from being a function and only something that's JSON serializable
const genRenderOptions = <D extends object>(
    options: RenderOptionsAccessor<any> | RenderOptions | undefined,
    og: any
) => {
    if (_.isFunction(options)) {
        return options(og) as D;
    }

    return options as D;
};

function TableCell<D extends object = {}>({ cell }: { cell: Cell<D> }) {
    const { column, value, row } = cell;
    const isForbiddenObject = !ALLOW_OBJECTS_FOR_RENDER_TYPE.includes(column?.renderType);

    if (
        // use lodash isObject since null is an object too if you use typeof
        (_.isObject(value) && isForbiddenObject) ||
        value === ''
    ) {
        // alert the caller to the fact that they provided an object
        // and didn't make it part of the allowed objects array on line 13
        // eslint-disable-next-line no-console
        if (value !== '') console.warn('Render two dashes because not allowed object', value);
        return <TwoDashes />;
    }

    if (column.renderType) {
        switch (column.renderType) {
            case 'ruleType': {
                return <RuleType type={value as RuleTypeOptions['type']} />;
            }
            case 'link': {
                if (!column.renderOptions) break;
                return renderLinkButton(value, column.renderOptions as LinkRenderOptions);
            }
            case 'badge': {
                return value ? <Badge type={(column.renderOptions as BadgeRenderOptions)?.type}>{value}</Badge> : null;
            }
            case 'conclusion': {
                const renderOptions = genRenderOptions<ConclusionRenderOptions>(
                    cell.column.renderOptions,
                    cell.row.original
                );
                return (
                    <FormattedConclusion
                        value={value}
                        score={cell?.row?.original?.actions?.[0]?.values?.[0] || renderOptions?.score}
                    />
                );
            }
            case 'progressBar': {
                return renderProgressBar(value, column.renderOptions as ProgressBarRenderOptions);
            }
            case 'currency': {
                // TODO: modify to use reusable component with format from config
                return <DashOrEllipText rawValue={NumberService.getLocaleCurrency(value)} />;
            }
            case 'overrideCell': {
                return <>{cell.render('Cell', { ...value })}</>;
            }
            case 'modifiedIcon': {
                return renderModifiedIcon(value);
            }
            case 'datetime': {
                const valToRender = DateService.getDateTime(value);
                return valToRender === null ? <TwoDashes /> : <DashOrEllipText rawValue={valToRender} />;
            }
            case 'date': {
                const valToRender = DateService.getDate(value);
                return valToRender === null ? <TwoDashes /> : <DashOrEllipText rawValue={valToRender} />;
            }
            case 'trashIcon': {
                const renderOptions = genRenderOptions<TrashIconRenderOptions>(
                    cell.column.renderOptions,
                    cell.row.original
                );

                if (renderOptions.hideTrash) {
                    return null;
                }

                return renderTrashIcon(value, renderOptions, cell.row.original);
            }

            case 'checkbox': {
                return <div className={styles.checkbox}>{cell.render('Cell', { ...value })}</div>;
            }

            case 'commentIcon': {
                return value === null ? <TwoDashes /> : renderCommentIcon(value);
            }
            case 'bcatsMasked': {
                return <BcatsMaskedField value={value} rowId={row.id} />;
            }
            case 'titleCase': {
                return renderTitleCase(value, column.renderOptions as TitleCaseRenderOptions);
            }
            default:
                return renderDefault(cell);
        }
    }

    return renderDefault(cell);
}

export default TableCell;
